import React, { useState } from 'react';
import emailjs from '@emailjs/browser';

const QuoteForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    referralSource: '',
    services: {
      lift: false,
      staircase: false,
      flooring: false,
      joinery: false,
      aluminum: false
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (service) => {
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        [service]: !prev.services[service]
      }
    }));
  };

  const generateUniqueID = (referralSource) => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    
    // Map referral source to code
    const referralMap = {
      'Online': 's0',
      'SL1': 's1',
      'SL2': 's2',
      'SL3': 's3',
      'SL4': 's4',
      'Luxia1': 'l1',
      'Luxia2': 'l2',
      'Luxia3': 'l3',
      'Luxia4': 'l4',
      'BLL1': 'b1',
      'BLL2': 'b2',
      'DW1': 'd1',
      'DW2': 'd2'
    };
    
    const referralCode = referralMap[referralSource] || '';
    const baseID = `SL-${dateStr}-${timeStr}-${random}`;
    
    return referralCode ? `${baseID}-${referralCode}` : baseID;
  };

  const getEmailAddresses = () => {
    const { lift, staircase, flooring, joinery, aluminum } = formData.services;
    const emailAddresses = [];
    
    // Collect all selected service email addresses
    if (flooring) {
      emailAddresses.push('account@luxia.au');
    }
    if (joinery) {
      emailAddresses.push('sales@boolala.com.au');
    }
    if (aluminum) {
      emailAddresses.push('info@aluminum.com');
    }
    if (lift || staircase) {
      emailAddresses.push('info@stairlifte.com.au');
    }
    
    // Default if no specific service selected
    if (emailAddresses.length === 0) {
      emailAddresses.push('info@stairlifte.com.au');
    }
    
    return emailAddresses;
  };

  const formatEmailBody = (uniqueID) => {
    const selectedServices = Object.entries(formData.services)
      .filter(([_, selected]) => selected)
      .map(([key, _]) => {
        const labels = {
          lift: 'Lift',
          staircase: 'Staircase',
          flooring: 'Flooring',
          joinery: 'Joinery',
          aluminum: 'Door & Window'
        };
        return labels[key];
      })
      .join(', ');

    return `Unique Identification Number: ${uniqueID}

Personal Information:
- First Name: ${formData.firstName}
- Last Name: ${formData.lastName}
- Email Address: ${formData.email}
- Phone Number: ${formData.phone}

Inquiry Details:
- Subject: ${formData.subject}
- Message: ${formData.message}

Services of Interest: ${selectedServices || 'None selected'}

Where did you hear about us: ${formData.referralSource || 'Not specified'}

---
This inquiry was submitted through the STAIRLIFTÉ website.`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Generate unique ID with referral source code
      const uniqueID = generateUniqueID(formData.referralSource);
      
      // Get all email addresses based on checkbox selections
      const emailAddresses = getEmailAddresses();
      
      // Format email body
      const emailBody = formatEmailBody(uniqueID);
      const emailSubject = `Quote Request - ${uniqueID} - ${formData.subject}`;
      
      // Get selected services as a string
      const selectedServices = Object.entries(formData.services)
        .filter(([_, selected]) => selected)
        .map(([key, _]) => {
          const labels = {
            lift: 'Lift',
            staircase: 'Staircase',
            flooring: 'Flooring',
            joinery: 'Joinery',
            aluminum: 'Door & Window'
          };
          return labels[key];
        })
        .join(', ');

      // EmailJS credentials
      const SERVICE_ID = (process.env.REACT_APP_EMAILJS_SERVICE_ID || '').trim();
      const TEMPLATE_ID = (process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '').trim();
      const PUBLIC_KEY = (process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '').trim();

      // Validate that all credentials are set
      if (!SERVICE_ID || SERVICE_ID === 'YOUR_SERVICE_ID' || SERVICE_ID === '') {
        throw new Error('EmailJS Service ID is not configured. Please check your .env file.');
      }
      if (!TEMPLATE_ID || TEMPLATE_ID === 'YOUR_TEMPLATE_ID' || TEMPLATE_ID === '') {
        throw new Error('EmailJS Template ID is not configured. Please check your .env file.');
      }
      if (!PUBLIC_KEY || PUBLIC_KEY === 'YOUR_PUBLIC_KEY' || PUBLIC_KEY === '') {
        throw new Error('EmailJS Public Key is not configured. Please check your .env file.');
      }

      // Initialize EmailJS with the public key
      emailjs.init(PUBLIC_KEY);

      // Send emails to all selected service addresses
      const emailPromises = emailAddresses.map(emailAddress => {
        const templateParams = {
          to_email: emailAddress,
          to_name: 'STAIRLIFTÉ Team',
          from_name: `${formData.firstName} ${formData.lastName}`,
          from_email: formData.email,
          subject: emailSubject,
          message: emailBody,
          unique_id: uniqueID,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          inquiry_subject: formData.subject,
          inquiry_message: formData.message,
          services: selectedServices || 'None selected',
          referral_source: formData.referralSource || 'Not specified',
          reply_to: formData.email
        };

        return emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams);
      });

      // Wait for all emails to be sent
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(emailPromises);
      
      // Check if all emails were sent successfully
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (failed > 0) {
        // Some emails failed
        const failedAddresses = emailAddresses.filter((_, index) => results[index].status === 'rejected');
        const successMessage = `Thank you for your inquiry! Your Unique Identification Number is: ${uniqueID}. ${successful} email(s) sent successfully. ${failed} email(s) failed to send.`;
        console.error('Some emails failed to send:', failedAddresses);
        setSubmitStatus({ 
          type: 'error', 
          message: successMessage 
        });
      } else {
        // All emails sent successfully
        const emailCount = emailAddresses.length;
        const emailList = emailAddresses.join(', ');
        const successMessage = emailCount > 1 
          ? `Thank you for your inquiry! Your Unique Identification Number is: ${uniqueID}. Emails have been sent to: ${emailList}`
          : `Thank you for your inquiry! Your Unique Identification Number is: ${uniqueID}. Email has been sent to: ${emailList}`;
        
        setSubmitStatus({ type: 'success', message: successMessage });
      }
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        referralSource: '',
        services: {
          lift: false,
          staircase: false,
          flooring: false,
          joinery: false,
          aluminum: false
        }
      });
      
      // Close form after 3 seconds
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Email sending failed:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to send email. Please try again or contact us directly.';
      
      if (error.message) {
        if (error.message.includes('Public Key')) {
          errorMessage = 'Invalid Public Key. Please check your .env file and ensure REACT_APP_EMAILJS_PUBLIC_KEY is set correctly.';
        } else if (error.message.includes('Service ID')) {
          errorMessage = 'Invalid Service ID. Please check your .env file and ensure REACT_APP_EMAILJS_SERVICE_ID is set correctly.';
        } else if (error.message.includes('Template ID')) {
          errorMessage = 'Invalid Template ID. Please check your .env file and ensure REACT_APP_EMAILJS_TEMPLATE_ID is set correctly.';
        } else if (error.message.includes('not configured')) {
          errorMessage = error.message;
        } else if (error.text) {
          errorMessage = `EmailJS Error: ${error.text}`;
        }
      }
      
      setSubmitStatus({ 
        type: 'error', 
        message: errorMessage 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold text-premium-dark">Request a Quote</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-premium-dark transition-colors"
            aria-label="Close form"
          >
            <i className="fa-solid fa-times text-2xl"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {submitStatus && (
            <div className={`mb-4 p-4 rounded-lg ${
              submitStatus.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <div className="flex items-center space-x-2">
                <i className={`fa-solid ${submitStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                <p className="text-sm font-medium">{submitStatus.message}</p>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-premium-dark mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-premium-gold focus:border-premium-gold outline-none"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-premium-dark mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-premium-gold focus:border-premium-gold outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-premium-dark mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-premium-gold focus:border-premium-gold outline-none"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-premium-dark mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-premium-gold focus:border-premium-gold outline-none"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="subject" className="block text-sm font-medium text-premium-dark mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              required
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-premium-gold focus:border-premium-gold outline-none"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium text-premium-dark mb-2">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows="4"
              value={formData.message}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-premium-gold focus:border-premium-gold outline-none resize-none"
            ></textarea>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-premium-dark mb-3">
              Services of Interest
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: 'lift', label: 'Lift' },
                { key: 'staircase', label: 'Staircase' },
                { key: 'flooring', label: 'Flooring' },
                { key: 'joinery', label: 'Joinery' },
                { key: 'aluminum', label: 'Door & Window' }
              ].map((service) => (
                <label key={service.key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.services[service.key]}
                    onChange={() => handleCheckboxChange(service.key)}
                    className="w-4 h-4 text-premium-gold border-gray-300 rounded focus:ring-premium-gold"
                  />
                  <span className="text-sm text-gray-700">{service.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="referralSource" className="block text-sm font-medium text-premium-dark mb-2">
              Where did you hear about us?
            </label>
            <select
              id="referralSource"
              name="referralSource"
              value={formData.referralSource}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-premium-gold focus:border-premium-gold outline-none bg-white"
            >
              <option value="">Select an option</option>
              <option value="Online">Online</option>
              <option value="SL1">SL1</option>
              <option value="SL2">SL2</option>
              <option value="SL3">SL3</option>
              <option value="SL4">SL4</option>
              <option value="Luxia1">Luxia1</option>
              <option value="Luxia2">Luxia2</option>
              <option value="Luxia3">Luxia3</option>
              <option value="Luxia4">Luxia4</option>
              <option value="BLL1">BLL1</option>
              <option value="BLL2">BLL2</option>
              <option value="DW1">DW1</option>
              <option value="DW2">DW2</option>
            </select>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-premium-gold text-premium-dark px-6 py-3 font-semibold hover:bg-premium-dark hover:text-white transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  <span>Sending...</span>
                </>
              ) : (
                <span>Submit Request</span>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border-2 border-premium-dark text-premium-dark px-6 py-3 font-semibold hover:bg-premium-dark hover:text-white transition-colors rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteForm;
